import {
    IProFormaGeneralInfo,
    IOperatingAndPermanentDebtInfo,
    IUsesSummary,
    ISourcesSummary,
    IOperatingPeriodCashFlows,
    IDispositionData,
    IProFormaReturnsData,
    IIncomeSummary,
} from 'views/ProFormaTable/types';
import { IProFormaExcelData } from './types';
import { exportProFormaToExcel } from './proFormaExcelExport';
import { formatBuildingInfoSheet } from './buildingInfoSheet';
import { formatSourcesUsesSheet } from './sourcesUsesSheet';
import { formatCashFlowSheet } from './cashFlowSheet';
import { formatDispositionSheet } from './dispositionSheet';
import { formatReturnsSummarySheet } from './returnsSummarySheet';
import { EXCEL_SHEET_NAMES } from './constants';
import { getUnitTypeOptions } from 'views/ProFormaTable/components/ProForma/ProFormaDisposition/helpers';
import { NON_APARTMENT_UNIT_TYPE_NAME, APARTMENT_UNIT_TYPE_NAME } from 'views/ProFormaTable/components/ProForma/ProFormaDisposition/constants';

export interface IExportProFormaToExcelParams {
    proForma: IProFormaGeneralInfo;
    operatingInfo: IOperatingAndPermanentDebtInfo;
    incomeSummary: IIncomeSummary;
    usesSummary: IUsesSummary;
    sourcesSummary: ISourcesSummary;
    cashFlows: (IOperatingPeriodCashFlows | null)[];
    disposition: IDispositionData | undefined;
    returns: IProFormaReturnsData;
    showCents?: boolean;
    includeGainLossToLease?: boolean;
}

export const handleExportProFormaToExcel = async ({
    proForma,
    operatingInfo,
    incomeSummary,
    usesSummary,
    sourcesSummary,
    cashFlows,
    disposition,
    returns,
    showCents = false,
    includeGainLossToLease = true,
}: IExportProFormaToExcelParams): Promise<void> => {
    const dealName = proForma.dealName || proForma.name || 'Pro Forma';
    const today = new Date();
    const dateStr = `${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}${today.getFullYear()}`;
    const fileName = `${dealName} - Pro Forma - ${dateStr}`;

    const excelData: IProFormaExcelData = {
        fileName,
        sheets: [
            {
                sheetName: EXCEL_SHEET_NAMES.BUILDING_INFO,
                sheetData: formatBuildingInfoSheet({
                    proForma,
                    operatingInfo,
                    totalRentableSf: incomeSummary?.totalRentableSf,
                }),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.SOURCES_USES,
                sheetData: formatSourcesUsesSheet({
                    dealName,
                    usesSummary,
                    sourcesSummary,
                    totalUnits: proForma.estimatedUnits,
                    grossSF: proForma.builtGrossSquareFootage,
                    totalRentableSf: incomeSummary?.totalRentableSf,
                    proFormaModelId: proForma.proFormaModelId,
                    showCents,
                }),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.CASH_FLOW,
                sheetData: formatCashFlowSheet({
                    dealName,
                    cashFlows,
                    operationBeginMonth: operatingInfo?.operationBeginMonth,
                    returns,
                    disposition,
                    usesSummary,
                    proFormaModelId: proForma.proFormaModelId,
                    showCents,
                    includeGainLossToLease,
                    incomeCalculationType: operatingInfo?.incomeCalculationType,
                }),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.DISPOSITION,
                sheetData: (() => {
                    const unitTypeOptions = getUnitTypeOptions(operatingInfo, proForma);
                    const showPerGrossSf = unitTypeOptions.some(opt => opt.value === NON_APARTMENT_UNIT_TYPE_NAME.GSF);
                    const showPerRentableSf = unitTypeOptions.some(
                        opt => opt.value === NON_APARTMENT_UNIT_TYPE_NAME.RSF || opt.value === APARTMENT_UNIT_TYPE_NAME.SF,
                    );
                    const showPerUnit = unitTypeOptions.some(opt => opt.value === APARTMENT_UNIT_TYPE_NAME.UNIT);

                    return formatDispositionSheet({
                        dealName,
                        disposition,
                        saleDate: proForma.saleDate,
                        saleMonth: proForma.saleMonth,
                        proFormaModelId: proForma.proFormaModelId,
                        showCents,
                        showPerGrossSf,
                        showPerRentableSf,
                        showPerUnit,
                    });
                })(),
            },
            {
                sheetName: EXCEL_SHEET_NAMES.RETURNS_SUMMARY,
                sheetData: formatReturnsSummarySheet({
                    dealName,
                    returns,
                    disposition,
                    operatingInfo,
                    proFormaModelId: proForma.proFormaModelId,
                    cashFlows,
                    stabilizedMonth: operatingInfo?.stabilizedMonth,
                    showCents,
                }),
            },
        ],
    };

    await exportProFormaToExcel(excelData);
};
